import {
  DeviceType,
  OccupancyLineDeviceTypes,
  RoomRadarDeviceTypes,
} from "@tether-web-portal/types/DeviceType";
import { RoomLocationWithReading } from "@tether-web-portal/types/RoomLocation";
import { RoomWithoutLocationReadings } from "@tether-web-portal/types/Room";

import {
  IOccupancyPageFilters,
  IRoomOccupancyAggregates,
  OCCUPANCY_CHILDREN_DEVICE_TYPES,
  OCCUPANCY_DEVICE_TYPES,
  OccupancyFilterType,
  RoomAggregatedData,
} from "@tether-web-portal/web-workers/occupancy/types/OccupancyWorker";

export const makeFloorLevelSpaceName = (floorIndex: number): string =>
  floorIndex === 0 ? "Ground Level" : `Level ${floorIndex}`;

export const isRoomCapacityAble = (room?: RoomWithoutLocationReadings): boolean => {
  if (!room) return false;
  return room.roomLocations.some((x) => RoomRadarDeviceTypes.has(x.device?.type as DeviceType));
};

/**
 * True when the room location is a child occupancy device (REGION or LINE)
 * that has at least one sample reported. Used to decide whether to include
 * a child row in the utilisation table.
 */
export const checkIfRoomLocationHasActiveOccupancyChildrenDevice = (
  roomLocation: RoomLocationWithReading,
): boolean =>
  !!roomLocation.device &&
  OCCUPANCY_CHILDREN_DEVICE_TYPES.includes(roomLocation.device.type as DeviceType) &&
  Object.keys(roomLocation.device.lastSample?.sample ?? {}).length > 0;

export const checkIfRoomHasOccupancyDevices = (room?: RoomWithoutLocationReadings): boolean =>
  (room?.roomLocations ?? []).some(
    (x) => x.device && OCCUPANCY_DEVICE_TYPES.includes(x.device.type as DeviceType),
  );

export const applyPageLevelFilters = (
  rooms: IRoomOccupancyAggregates[],
  filters: IOccupancyPageFilters | undefined,
): IRoomOccupancyAggregates[] => {
  if (!filters) return rooms;
  if (filters.spaceType === OccupancyFilterType.FLOOR)
    return rooms.filter((x) => x.floorIndex === filters.floorIndex);
  if (filters.spaceType === OccupancyFilterType.ROOM)
    return rooms.filter((x) => x.id === filters.spaceId);
  return rooms;
};

export const getRoomsWithAggregates = (
  rooms: RoomWithoutLocationReadings[] = [],
  roomAggregates: RoomAggregatedData[],
): IRoomOccupancyAggregates[] =>
  rooms
    .filter(checkIfRoomHasOccupancyDevices)
    .map(
      (r) =>
        ({
          ...r,
          aggregations: roomAggregates.find((x) => x?.roomId === r.id)?.aggregatedData ?? [],
        }) as IRoomOccupancyAggregates,
    );

/**
 * True when the room location is a LINE (people counter / entry-exit) device
 * rather than a REGION. Used by the utilisation-table child rows to decide
 * which columns (dwell time vs total in/out) apply.
 */
export const isLineOccupancyDevice = (roomLocation: RoomLocationWithReading): boolean =>
  OccupancyLineDeviceTypes.has(roomLocation.device?.type as DeviceType);
